/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Physics domain - Shared physics calculations and utilities
 * physics.common.ts - Shared physics calculations and movement simulation functions
 */

import type { PhysicsInput, Vec2 } from "./physics.types";

export type { Vec2, PhysicsInput };

export type Player = {
  id: string;
  name: string;
  pos: Vec2;
  vel: Vec2;
  rotation: number;
  targetRotation: number;
  angularVelocity: number;
  lastDirection: Vec2;
  input: PhysicsInput;
  lastAcknowledgedSequence: number;
  health: number;
  maxHealth: number;
  respawnTimer: number;
  lastShootAt: number;
  lastBoostAt: number;
  boostTimer: number;
  kills: number;
  deaths: number;
  pelletsCollected: number;
  scale: number;
  damageMultiplier: number;
  speedMultiplier: number;
  magnetRange: number;
};

export type Bullet = {
  id: string;
  ownerId: string;
  pos: Vec2;
  vel: Vec2;
  age: number;
  shooterScale: number;
  damage: number;
};

export type Asteroid = {
  id: string;
  pos: Vec2;
  vel: Vec2;
  sizeLevel: number;
  rotation: number;
  rotationSpeed: number;
  health: number;
  maxHealth: number;
};

// Import physics constants from config.ts
import {
  BULLET_LIFETIME,
  BULLET_RADIUS,
  BULLET_SPEED,
  FRICTION,
  MAX_ANGULAR_VELOCITY,
  MAX_SPEED,
  MAX_SPEED_MULTIPLIER_CAP,
  MOUSE_ROTATION_SPEED,
  PLAYER_RADIUS,
  ROTATION_SNAP_THRESHOLD,
  SHOOT_COOLDOWN,
  SPEED_AFFECTS_ATTACK_RATE,
  SPEED_AFFECTS_BULLET_LIFETIME,
  SPEED_AFFECTS_BULLET_VELOCITY,
  SPEED_ATTACK_RATE_SCALING,
  SPEED_BULLET_LIFETIME_SCALING,
  SPEED_BULLET_VELOCITY_SCALING,
  THRUST,
  WORLD,
} from "@game/config";
import {
  applyBoostToMaxSpeed,
  applyBoostToThrust,
  enforceBulletMinimumSpeed,
  updateBoostTimer,
} from "@game/domain";

// Re-export constants for convenience
export const PHYSICS_CONSTANTS = {
  THRUST,
  FRICTION,
  MAX_SPEED,
  BULLET_SPEED,
  BULLET_LIFETIME,
  BULLET_RADIUS,
  PLAYER_RADIUS,
  WORLD,
  SHOOT_COOLDOWN,
  ANGULAR_VELOCITY: MAX_ANGULAR_VELOCITY,
} as const;

// Mathematical constants
export const TAU = Math.PI * 2;

// World coordinate wrapping utility
export function wrapCoordinate(coord: number, worldSize: number): number {
  if (coord < 0) return coord + worldSize;
  if (coord >= worldSize) return coord - worldSize;
  return coord;
}

/**
 * Calculates wrapped distance between two points considering world boundaries
 * Used across collision detection, mine explosions, pellet attraction, etc.
 * Eliminates duplicate implementations across server modules
 * @param x1 - First point x coordinate
 * @param y1 - First point y coordinate
 * @param x2 - Second point x coordinate
 * @param y2 - Second point y coordinate
 * @param worldW - World width (defaults to 10000)
 * @param worldH - World height (defaults to 10000)
 * @returns Object with wrapped dx, dy, and distance
 */
export function calculateWrappedDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  worldW = 10000, // WORLD.w default
  worldH = 10000, // WORLD.h default
): { dx: number; dy: number; distance: number } {
  let dx = x1 - x2;
  let dy = y1 - y2;

  // Handle wrapping for collision detection
  if (Math.abs(dx) > worldW / 2) {
    dx = dx > 0 ? dx - worldW : dx + worldW;
  }
  if (Math.abs(dy) > worldH / 2) {
    dy = dy > 0 ? dy - worldH : dy + worldH;
  }

  const distance = Math.sqrt(dx * dx + dy * dy);
  return { dx, dy, distance };
}

/**
 * Calculates wrapped distance between two Vec2 positions (simplified interface)
 * @param pos1 - First position
 * @param pos2 - Second position
 * @param worldW - World width (defaults to 10000)
 * @param worldH - World height (defaults to 10000)
 * @returns Distance considering world wrapping
 */
export function calculateWrappedDistanceVec2(
  pos1: Vec2,
  pos2: Vec2,
  worldW = 10000,
  worldH = 10000,
): number {
  const { distance } = calculateWrappedDistance(
    pos1.x,
    pos1.y,
    pos2.x,
    pos2.y,
    worldW,
    worldH,
  );
  return distance;
}

// Angle normalization utility (shared)
export function normPi(a: number): number {
  // Normalize to [-π, π]
  a = (a + Math.PI) % (2 * Math.PI);
  if (a < 0) a += 2 * Math.PI;
  return a - Math.PI;
}

// Shortest angular distance utility (shared)
export function shortestDelta(from: number, to: number): number {
  return normPi(to - from);
}

// Rotate towards target angle utility (shared)
export function rotateTowardsAngle(
  current: number,
  target: number,
  dt: number,
  maxSpeed: number,
  snap: number = 1e-3,
): { rotation: number; angularVelocity: number } {
  const d = shortestDelta(current, target);
  if (Math.abs(d) <= snap) {
    return { rotation: normPi(target), angularVelocity: 0 };
  }

  const step = Math.sign(d) * Math.min(Math.abs(d), Math.max(0, maxSpeed * dt));
  const next = normPi(current + step);
  return { rotation: next, angularVelocity: step / dt };
}

// Sophisticated rotation system (shared between client and server)
export function updatePlayerRotationShared(
  player: Player,
  input: PhysicsInput,
  dt: number,
): Player {
  const result = { ...player };

  // Use aimDirection for mouse/touch aim (rotation continues even in strafe mode)
  if (input.mouseActive && input.aimDirection) {
    // Calculate target angle from aim direction (already a unit vector)
    const targetAngle = Math.atan2(input.aimDirection.y, input.aimDirection.x);
    result.targetRotation = normPi(targetAngle);
  }
  // else: HOLD previous target (do nothing); servo will converge

  // Always use servo for smooth rotation (fast for mouse, normal for keyboard)
  const maxSpeed = input.mouseActive
    ? MOUSE_ROTATION_SPEED
    : MAX_ANGULAR_VELOCITY;
  const { rotation, angularVelocity } = rotateTowardsAngle(
    result.rotation,
    result.targetRotation,
    dt,
    maxSpeed,
    ROTATION_SNAP_THRESHOLD,
  );

  result.rotation = rotation;
  result.angularVelocity = angularVelocity; // keep for HUD/telemetry

  return result;
}

// Shooting cooldown check (shared) - now with speed multiplier scaling
export function canPlayerShoot(player: Player, currentTime: number): boolean {
  let effectiveCooldown = SHOOT_COOLDOWN;

  if (SPEED_AFFECTS_ATTACK_RATE) {
    const cappedSpeedMult = Math.min(
      player.speedMultiplier || 1.0,
      MAX_SPEED_MULTIPLIER_CAP,
    );
    const speedScaling = 1 + (cappedSpeedMult - 1) * SPEED_ATTACK_RATE_SCALING;
    effectiveCooldown = SHOOT_COOLDOWN / speedScaling;
  }

  return currentTime - player.lastShootAt > effectiveCooldown * 1000;
}

// Export boost functions from boost module
export { canPlayerBoost } from "@game/domain";

// Bot-specific shooting cooldown check with speed multiplier scaling
export function canBotShoot(
  bot: { speedMultiplier?: number; lastShootAt: number },
  currentTime: number,
): boolean {
  let effectiveCooldown = SHOOT_COOLDOWN;

  if (SPEED_AFFECTS_ATTACK_RATE) {
    const cappedSpeedMult = Math.min(
      bot.speedMultiplier || 1.0,
      MAX_SPEED_MULTIPLIER_CAP,
    );
    const speedScaling = 1 + (cappedSpeedMult - 1) * SPEED_ATTACK_RATE_SCALING;
    effectiveCooldown = SHOOT_COOLDOWN / speedScaling;
  }

  return currentTime - bot.lastShootAt > effectiveCooldown * 1000;
}

// Wrapped distance calculation for collision detection
export function getWrappedDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  worldW: number,
  worldH: number,
): { dx: number; dy: number; distance: number } {
  let dx = x2 - x1;
  let dy = y2 - y1;

  // Handle wrapping for distance calculation
  if (Math.abs(dx) > worldW / 2) {
    dx = dx > 0 ? dx - worldW : dx + worldW;
  }
  if (Math.abs(dy) > worldH / 2) {
    dy = dy > 0 ? dy - worldH : dy + worldH;
  }

  const distance = Math.sqrt(dx * dx + dy * dy);
  return { dx, dy, distance };
}

// Player physics step function
// IMMUTABLE: Returns new player object with new nested objects for delta compression
export function stepPlayer(
  player: Player,
  input: PhysicsInput,
  dt: number,
): Player {
  let result = { ...player };
  const { THRUST, FRICTION, MAX_SPEED, WORLD } = PHYSICS_CONSTANTS;

  // Handle rotation with sophisticated targeting system
  // This is now shared between client and server for consistent prediction
  result = updatePlayerRotationShared(result, input, dt);

  // STRAFE MODE: Use locked thrust angle if set, otherwise use current rotation
  const thrustAngle = input.lockedThrustAngle ?? result.rotation;

  // Calculate total thrust vector from all inputs to prevent diagonal movement exploit
  let totalThrustX = 0;
  let totalThrustY = 0;
  let baseThrust = THRUST * (result.speedMultiplier || 1.0);

  // Apply boost multiplier using boost module
  const boostedThrust = applyBoostToThrust(baseThrust, result);
  baseThrust = boostedThrust;

  // Apply thrust based on movement vector
  // input.movement.y = forward/back (-1..1)
  // input.movement.x = strafe left/right (-1..1)

  // Forward/backward thrust along ship/locked angle
  totalThrustX += Math.cos(thrustAngle) * input.movement.y * baseThrust;
  totalThrustY += Math.sin(thrustAngle) * input.movement.y * baseThrust;

  // Strafe thrust perpendicular to thrust angle (locked or current rotation)
  const perpAngle = thrustAngle + Math.PI / 2;
  totalThrustX += Math.cos(perpAngle) * input.movement.x * baseThrust * 0.7;
  totalThrustY += Math.sin(perpAngle) * input.movement.x * baseThrust * 0.7;

  // Normalize total thrust to prevent diagonal movement being faster
  const totalThrustMagnitude = Math.sqrt(
    totalThrustX * totalThrustX + totalThrustY * totalThrustY,
  );
  if (totalThrustMagnitude > baseThrust) {
    const scale = baseThrust / totalThrustMagnitude;
    totalThrustX *= scale;
    totalThrustY *= scale;
  }

  // IMMUTABLE: Create new velocity object
  let newVelX = result.vel.x + totalThrustX * dt;
  let newVelY = result.vel.y + totalThrustY * dt;

  // Apply friction
  newVelX *= FRICTION;
  newVelY *= FRICTION;

  // Cap maximum speed using boost module
  const maxSpeed = applyBoostToMaxSpeed(MAX_SPEED, result);
  const currentSpeed = Math.sqrt(newVelX * newVelX + newVelY * newVelY);
  if (currentSpeed > maxSpeed) {
    const scale = maxSpeed / currentSpeed;
    newVelX *= scale;
    newVelY *= scale;
  }

  // Update boost timer using boost module
  const updatedBoostData = updateBoostTimer(result, dt);
  result.boostTimer = updatedBoostData.boostTimer;

  // IMMUTABLE: Create new position object
  let newPosX = result.pos.x + newVelX * dt;
  let newPosY = result.pos.y + newVelY * dt;

  // Wrap position
  newPosX = wrapCoordinate(newPosX, WORLD.w);
  newPosY = wrapCoordinate(newPosY, WORLD.h);

  // IMMUTABLE: Assign new nested objects
  result.vel = { x: newVelX, y: newVelY };
  result.pos = { x: newPosX, y: newPosY };
  result.lastDirection = {
    x: Math.cos(result.rotation),
    y: Math.sin(result.rotation),
  };

  return result;
}

// Bullet physics step function
// IMMUTABLE: Returns new bullet object with new pos object for delta compression
export function stepBullet(bullet: Bullet, dt: number): Bullet {
  const result = { ...bullet };
  const { WORLD } = PHYSICS_CONSTANTS;

  // Age the bullet
  result.age += dt;

  // IMMUTABLE: Create new position object
  let newPosX = result.pos.x + result.vel.x * dt;
  let newPosY = result.pos.y + result.vel.y * dt;

  // Wrap bullet position
  newPosX = wrapCoordinate(newPosX, WORLD.w);
  newPosY = wrapCoordinate(newPosY, WORLD.h);

  // IMMUTABLE: Assign new pos object
  result.pos = { x: newPosX, y: newPosY };

  return result;
}

// Calculate effective bullet lifetime based on speed scaling
export function calculateBulletLifetime(bullet: Bullet): number {
  if (!SPEED_AFFECTS_BULLET_LIFETIME || SPEED_BULLET_LIFETIME_SCALING === 0) {
    return PHYSICS_CONSTANTS.BULLET_LIFETIME;
  }

  // Calculate bullet speed magnitude
  const bulletSpeed = Math.sqrt(
    bullet.vel.x * bullet.vel.x + bullet.vel.y * bullet.vel.y,
  );

  // Calculate speed scaling (how much faster this bullet is compared to base speed)
  const speedScaling = bulletSpeed / PHYSICS_CONSTANTS.BULLET_SPEED;

  // Apply inverse scaling to lifetime (faster bullets = shorter lifetime)
  // Use the same scaling formula as bullet velocity for consistency
  const lifetimeScaling =
    1 + (speedScaling - 1) * SPEED_BULLET_LIFETIME_SCALING;

  return PHYSICS_CONSTANTS.BULLET_LIFETIME / lifetimeScaling;
}

// Check if bullet should be removed due to age
export function shouldRemoveBullet(bullet: Bullet): boolean {
  const effectiveLifetime = calculateBulletLifetime(bullet);
  return bullet.age > effectiveLifetime;
}

// Create a new bullet from player state - now with speed multiplier scaling
export function createBullet(
  player: Player,
  bulletId: string,
  _currentTime: number,
  damage: number,
): Bullet {
  const { BULLET_SPEED } = PHYSICS_CONSTANTS;

  // Calculate bullet speed with speed multiplier scaling
  let bulletSpeed = BULLET_SPEED;
  if (SPEED_AFFECTS_BULLET_VELOCITY) {
    const cappedSpeedMult = Math.min(
      player.speedMultiplier || 1.0,
      MAX_SPEED_MULTIPLIER_CAP,
    );
    const speedScaling =
      1 + (cappedSpeedMult - 1) * SPEED_BULLET_VELOCITY_SCALING;
    bulletSpeed = BULLET_SPEED * speedScaling;
  }

  // Shoot in the direction the player is facing
  const dirX = Math.cos(player.rotation);
  const dirY = Math.sin(player.rotation);

  // Calculate bullet velocity with player velocity added
  let bulletVelX = dirX * bulletSpeed + player.vel.x;
  let bulletVelY = dirY * bulletSpeed + player.vel.y;

  // Ensure minimum bullet speed using boost module
  const correctedVelocity = enforceBulletMinimumSpeed(
    bulletVelX,
    bulletVelY,
    dirX,
    dirY,
  );
  bulletVelX = correctedVelocity.x;
  bulletVelY = correctedVelocity.y;

  return {
    id: bulletId,
    ownerId: player.id,
    pos: {
      x: player.pos.x + dirX * 20,
      y: player.pos.y + dirY * 20,
    }, // Spawn slightly ahead
    vel: {
      x: bulletVelX,
      y: bulletVelY,
    },
    age: 0,
    shooterScale: (player.scale || 1.0) / 3, // One third the player scale
    damage,
  };
}

// Check collision between two circular entities
export function checkCircleCollision(
  pos1: Vec2,
  radius1: number,
  pos2: Vec2,
  radius2: number,
  worldW: number = PHYSICS_CONSTANTS.WORLD.w,
  worldH: number = PHYSICS_CONSTANTS.WORLD.h,
): boolean {
  const { distance } = getWrappedDistance(
    pos1.x,
    pos1.y,
    pos2.x,
    pos2.y,
    worldW,
    worldH,
  );
  return distance < radius1 + radius2;
}

// ============================================================================
// PER-TICK PHYSICS (matches server's computeMovementVelocity exactly)
// ============================================================================

export interface TickMovementInput {
  rotation: number;
  currentVelX: number;
  currentVelY: number;
  posX: number;
  posY: number;
  keyForward: boolean;
  keyBack: boolean;
  keyLeft: boolean;
  keyRight: boolean;
  thrustPerTick: number;
  maxSpeed: number;
  strafeMultiplier: number;
  friction: number;
  worldWidth: number;
  worldHeight: number;
}

export interface TickMovementResult {
  posX: number;
  posY: number;
  velX: number;
  velY: number;
}

export function computeTickMovement(
  input: TickMovementInput,
): TickMovementResult {
  const forwardInput = Number(input.keyForward) - Number(input.keyBack);
  const strafeInput = Number(input.keyRight) - Number(input.keyLeft);

  let thrustX = Math.cos(input.rotation) * forwardInput * input.thrustPerTick;
  let thrustY = Math.sin(input.rotation) * forwardInput * input.thrustPerTick;

  if (strafeInput !== 0) {
    const perpendicular = input.rotation + Math.PI / 2;
    thrustX +=
      Math.cos(perpendicular) *
      strafeInput *
      input.thrustPerTick *
      input.strafeMultiplier;
    thrustY +=
      Math.sin(perpendicular) *
      strafeInput *
      input.thrustPerTick *
      input.strafeMultiplier;
  }

  const thrustMagnitude = Math.hypot(thrustX, thrustY);
  if (thrustMagnitude > input.thrustPerTick && input.thrustPerTick > 0) {
    const scale = input.thrustPerTick / thrustMagnitude;
    thrustX *= scale;
    thrustY *= scale;
  }

  let velX = (input.currentVelX + thrustX) * input.friction;
  let velY = (input.currentVelY + thrustY) * input.friction;

  const speed = Math.hypot(velX, velY);
  if (speed > input.maxSpeed && input.maxSpeed > 0) {
    const scale = input.maxSpeed / speed;
    velX *= scale;
    velY *= scale;
  }

  const posX = wrapCoordinate(input.posX + velX, input.worldWidth);
  const posY = wrapCoordinate(input.posY + velY, input.worldHeight);

  return { posX, posY, velX, velY };
}

export interface TickRotationInput {
  currentRotation: number;
  mouseScreenX: number;
  mouseScreenY: number;
  canvasWidth: number;
  canvasHeight: number;
  maxRotationPerTick: number;
  snapThreshold: number;
}

export interface TickRotationResult {
  rotation: number;
  targetRotation: number;
  angularVelocity: number;
}

export function computeTickRotation(
  input: TickRotationInput,
): TickRotationResult {
  const centerX = input.canvasWidth / 2;
  const centerY = input.canvasHeight / 2;
  const deltaX = input.mouseScreenX - centerX;
  const deltaY = input.mouseScreenY - centerY;

  const targetRotation =
    deltaX === 0 && deltaY === 0
      ? normPi(-Math.PI / 2)
      : normPi(Math.atan2(deltaY, deltaX));

  const delta = shortestDelta(input.currentRotation, targetRotation);
  if (Math.abs(delta) <= input.snapThreshold) {
    return {
      rotation: normPi(targetRotation),
      targetRotation,
      angularVelocity: 0,
    };
  }

  const step =
    Math.sign(delta) *
    Math.min(Math.abs(delta), Math.max(0, input.maxRotationPerTick));
  return {
    rotation: normPi(input.currentRotation + step),
    targetRotation,
    angularVelocity: step * 60,
  };
}

// ============================================================================
// STRAFE LOCK (matches server's resolveStrafeLock exactly)
// ============================================================================

export function resolveStrafeLock(args: {
  keyShift: boolean;
  previousKeyShift: boolean;
  currentRotation: number;
  existingLockedAngle?: number;
}): { nextLockedAngle?: number; thrustAngle?: number } {
  if (!args.keyShift) {
    return {
      nextLockedAngle: undefined,
      thrustAngle: undefined,
    };
  }

  if (!args.previousKeyShift || typeof args.existingLockedAngle !== "number") {
    return {
      nextLockedAngle: args.currentRotation,
      thrustAngle: args.currentRotation,
    };
  }

  return {
    nextLockedAngle: args.existingLockedAngle,
    thrustAngle: args.existingLockedAngle,
  };
}

// Calculate angular velocity needed to face a target
export function calculateRotationToTarget(
  fromPos: Vec2,
  _fromRotation: number,
  targetPos: Vec2,
  worldW: number = PHYSICS_CONSTANTS.WORLD.w,
  worldH: number = PHYSICS_CONSTANTS.WORLD.h,
): number {
  const { dx, dy } = getWrappedDistance(
    fromPos.x,
    fromPos.y,
    targetPos.x,
    targetPos.y,
    worldW,
    worldH,
  );
  return Math.atan2(dy, dx);
}
